import type {
  AckResult,
  AttachmentUpload,
  PeerId,
  Quality,
  RecordingKind,
  RecordingState,
} from '@live-stream/shared'
import type { Auth } from '../auth.js'
import { hueOf, initialsOf } from '../avatar.js'
import type { HttpClient } from '../http.js'
import type { Media } from '../media/media.js'
import type { Room } from '../room.js'
import type { AppSocket } from '../socket.js'
import { computeLayout } from '../stage/layout.js'
import type { Stage } from '../stage/stage.js'
import { derived, patch, readonly, watch, writable, type ReadableStore } from '../store.js'
import { pickRecordingProfile, type CompositorSource, type RecordingProfile } from './compositor.js'
import { crashBuffer, type BufferedFile } from './crash-buffer.js'
import { RecordingEngine, type EngineProgress } from './engine.js'
import { createOffscreenVideo, type OffscreenVideo } from './offscreen-video.js'
import { PartUploader } from './uploader.js'

/**
 * Meeting recording, the client's side.
 *
 * The FACT comes from the room: everyone draws the red indicator from
 * `RoomState.recording`, and a person who arrives while it is on is asked
 * before they enter. The WORK happens in the recorder's own browser: the
 * engine paints the stage, mixes the voices, records each participant's
 * audio apart, and uploads it all in parts as it goes. Stopping waits for
 * the last part to land. A tab that dies leaves its parts in IndexedDB;
 * the next visit offers to finish sending them.
 */

export interface UnfinishedUpload extends BufferedFile {
  busy: boolean
  error: string | null
}

export interface RecordingControllerState {
  /** Ticks once a second while a recording runs, for the clock on the indicator. */
  now: number
  starting: boolean
  /** Stopped, and the last parts are on their way. */
  finishing: boolean
  progress: EngineProgress | null
  /** One line for the person: saved, or what went wrong. */
  notice: string | null
  /** The recording that just finished, for the "open recordings" link. */
  lastSavedId: string | null
  /** A recording started while we are here, or before we came; we have not yet said we stay. */
  consentNeededFor: string | null
  /** The frame the running recording is made in; null between recordings. */
  profile: RecordingProfile | null
  /** Parts a tab that died left on this device, still to be sent. */
  unfinished: UnfinishedUpload[]
}

export class Recording {
  private readonly store = writable<RecordingControllerState>({
    now: Date.now(),
    starting: false,
    finishing: false,
    progress: null,
    notice: null,
    lastSavedId: null,
    consentNeededFor: null,
    profile: null,
    unfinished: [],
  })
  readonly state: ReadableStore<RecordingControllerState> = readonly(this.store)
  /** The room's recording: on or off, since when, by whom. */
  readonly room: ReadableStore<RecordingState>
  readonly active: ReadableStore<boolean>
  /** This browser is the one recording. */
  readonly isRecorder: ReadableStore<boolean>
  readonly elapsedMs: ReadableStore<number>
  /** Cohosts and up may record, when the browser can — the server has the last word. */
  readonly canRecord: ReadableStore<boolean>

  /** Our engine, while we record. */
  private engine: RecordingEngine | null = null
  private ticker: ReturnType<typeof setInterval> | null = null
  private readonly consented = new Set<string>()
  private ownShare: OffscreenVideo | null = null
  /**
   * The meeting laid out for the recording's frame. The stage in the window
   * is as wide as the browser and as tall as it can afford — a band across
   * a 16:9 canvas, every tile smaller than it need be. The same scene, the
   * same rules, laid out for the canvas itself gives the recording its whole
   * frame: Meet's recording is the meeting in 16:9, not a picture of
   * somebody's window.
   */
  private readonly layout: ReadableStore<ReturnType<typeof computeLayout> | null>

  constructor(
    private readonly http: HttpClient,
    private readonly auth: Auth,
    private readonly meeting: Room,
    private readonly media: Media,
    private readonly stage: Stage,
  ) {
    const r = meeting.state
    this.room = derived([r], (s) => s.room.recording)
    this.active = derived([this.room], (x) => x.active)
    this.isRecorder = derived([this.room, r], (x, s) => x.active && x.startedBy === s.me)
    this.elapsedMs = derived([this.room, this.store], (x, s) =>
      x.active && x.startedAt ? Math.max(0, s.now - x.startedAt) : 0,
    )
    const supported = RecordingEngine.supported()
    // Hosts and cohosts, and anyone a host has let record.
    this.canRecord = derived(
      [r],
      (s) =>
        supported &&
        (s.myRole === 'host' ||
          s.myRole === 'cohost' ||
          (s.me !== null && s.room.participants[s.me]?.recordGranted === true)),
    )
    const scene = derived([stage.state], (s) => s.scene)
    const profile = derived([this.store], (s) => s.profile)
    this.layout = derived([scene, profile], (sc, frame) =>
      sc && frame
        ? computeLayout({
            ...sc,
            containerW: frame.width,
            containerH: frame.height,
            isMobile: false,
            gap: 12,
          })
        : null,
    )

    watch(
      r,
      (s) => `${s.room.recording.active}|${s.room.recording.recordingId}|${s.me}|${s.phase}`,
      (_next, prev) => this.followFact(prev?.startsWith('true|') ?? false),
      { immediate: true },
    )
    watch(
      r,
      (s) => s.phase,
      (phase) => {
        if (phase !== 'idle' && phase !== 'ended') return
        // Leaving, or the meeting ending, with a recording running: finish it, do not lose it.
        if (this.engine && !this.store.get().finishing) void this.stop({ tellRoom: false })
        this.consented.clear()
        patch(this.store, { consentNeededFor: null })
      },
    )

    // The recording is the output, not the tile: a face that is a thumbnail
    // on the recorder's screen may be half the recording's frame. The layers
    // ITS layout calls for are asked of the SFU too, beside the stage's, for
    // as long as it runs.
    watch(
      this.layout,
      (l) => l,
      (layout) => {
        if (!layout) {
          media.setRecorderQualities(null)
          return
        }
        const me = r.get().me
        const wanted: Record<PeerId, Quality> = {}
        for (const t of layout.tiles) {
          if (t.kind === 'camera' && t.peerId !== me) wanted[t.peerId] = t.quality
        }
        media.setRecorderQualities(wanted)
      },
    )
  }

  private get s(): RecordingControllerState {
    return this.store.get()
  }

  // ----------------------------------------------------------- the fact

  private followFact(wasActive: boolean): void {
    const { room, me, phase } = this.meeting.state.get()
    const { active, recordingId, startedBy } = room.recording
    if (phase !== 'joined') return
    if (active && recordingId) {
      this.ticker ??= setInterval(() => patch(this.store, { now: Date.now() }), 1000)
      // Everyone but the recorder is asked, once per recording. The chime
      // says it started even to someone looking elsewhere.
      if (startedBy !== me && !this.consented.has(recordingId)) {
        patch(this.store, { consentNeededFor: recordingId })
        if (!wasActive) chime()
      }
    } else {
      if (this.ticker) clearInterval(this.ticker)
      this.ticker = null
      patch(this.store, { consentNeededFor: null })
      // The room says it is over — a host stopped it, or the server let go
      // — and we are still recording: stop, and keep what we have.
      if (this.engine && !this.s.finishing) void this.stop({ tellRoom: false })
    }
  }

  private request<T = void>(
    emit: (sock: AppSocket, done: (r: AckResult<T>) => void) => void,
  ): Promise<AckResult<T>> {
    return new Promise((resolve) => {
      const sock = this.meeting.socket.get()
      if (!sock)
        return resolve({ ok: false, error: { code: 'offline', message: 'Not connected.' } })
      emit(sock, resolve)
    })
  }

  /** "I stay." Logged with the time, once. */
  async consent(): Promise<void> {
    const recordingId = this.s.consentNeededFor
    if (!recordingId) return
    this.consented.add(recordingId)
    patch(this.store, { consentNeededFor: null })
    await this.request((s, done) => s.emit('recording:consent', { recordingId }, done))
  }

  /** "I leave." */
  declineAndLeave(): void {
    patch(this.store, { consentNeededFor: null })
    this.meeting.leave()
  }

  // ----------------------------------------------------------- the work

  /**
   * Our own share, for the recording. The presenter's stage shows a card
   * where everyone else sees their screen, so there is no element to paint
   * from; this one is never in the document and plays the capture stream
   * for the compositor alone. Without it the presentation was a black
   * rectangle in the presenter's own recording.
   */
  private ownShareVideo(): HTMLVideoElement | null {
    const stream = this.media.state.get().share?.stream ?? null
    if (!stream) {
      this.ownShare?.bind(null)
      return null
    }
    this.ownShare ??= createOffscreenVideo()
    this.ownShare.bind(stream)
    return this.ownShare.element
  }

  private releaseOwnShare(): void {
    this.ownShare?.stop()
    this.ownShare = null
  }

  private compositorSource(): CompositorSource {
    const people = () => this.meeting.state.get().room.participants
    return {
      tiles: () => this.layout.get()?.tiles ?? [],
      videoFor: (peerId, kind) => {
        if (kind === 'screen' && peerId === this.meeting.state.get().me) return this.ownShareVideo()
        return this.stage.videoElement(kind === 'screen' ? `screen:${peerId}` : peerId)
      },
      nameFor: (peerId) => people()[peerId]?.displayName ?? 'Someone',
      hueFor: (peerId) => hueOf(peerId),
      initialsFor: (peerId) => initialsOf(people()[peerId]?.displayName ?? '?'),
    }
  }

  private voices(): Array<{ peerId: string; name: string; track: MediaStreamTrack }> {
    const { me, room } = this.meeting.state.get()
    const out: Array<{ peerId: string; name: string; track: MediaStreamTrack }> = []
    const mine = this.media.local.state.get().audioTrack
    if (mine && me) {
      out.push({ peerId: me, name: room.participants[me]?.displayName ?? 'You', track: mine })
    }
    for (const remote of this.media.remoteList.get()) {
      const track = remote.stream.getAudioTracks()[0]
      if (track) {
        out.push({
          peerId: remote.peerId,
          name: room.participants[remote.peerId]?.displayName ?? 'Participant',
          track,
        })
      }
    }
    return out
  }

  private screenAudio(): MediaStreamTrack | null {
    for (const s of this.media.screenList.get()) {
      const track = s.stream.getAudioTracks()[0]
      if (track) return track
    }
    return this.media.state.get().share?.audio?.track ?? null
  }

  async start(): Promise<void> {
    if (this.engine || this.s.starting || this.active.get()) return
    if (!RecordingEngine.supported()) {
      patch(this.store, {
        notice: 'This browser cannot record. Use Chrome, Edge or Firefox on a computer.',
      })
      return
    }
    patch(this.store, { starting: true, notice: null })
    try {
      const started = await this.request<{ recordingId: string }>((s, done) =>
        s.emit('recording:start', done),
      )
      if (!started.ok) {
        patch(this.store, { notice: started.error.message })
        return
      }
      const recordingId = started.data.recordingId
      const frame = pickRecordingProfile({ cores: navigator.hardwareConcurrency || 4 })
      patch(this.store, { profile: frame })
      this.engine = new RecordingEngine({
        recordingId,
        roomSlug: this.meeting.state.get().slug ?? '',
        source: this.compositorSource(),
        profile: frame,
        voices: () => this.voices(),
        screenAudio: () => this.screenAudio(),
        begin: async (kind: RecordingKind, mimeType, participantPeerId) => {
          const res = await this.request<{ fileId: string; partBytes: number }>((s, done) =>
            s.emit(
              'recording:upload-begin',
              { recordingId, kind, mimeType, ...(participantPeerId ? { participantPeerId } : {}) },
              done,
            ),
          )
          if (!res.ok) throw new Error(res.error.message)
          return res.data
        },
        address: async (fileId, partNumber) => {
          const res = await this.request<AttachmentUpload>((s, done) =>
            s.emit('recording:upload-part', { fileId, partNumber }, done),
          )
          if (!res.ok) throw new Error(res.error.message)
          return res.data
        },
        complete: async (fileId, durationMs) => {
          const res = await this.request((s, done) =>
            s.emit(
              'recording:upload-complete',
              { fileId, ...(durationMs !== null ? { durationMs } : {}) },
              done,
            ),
          )
          if (!res.ok) throw new Error(res.error.message)
        },
        onProgress: (progress) => patch(this.store, { progress }),
      })
      await this.engine.start()
      patch(this.store, { lastSavedId: null })
    } catch (err) {
      patch(this.store, {
        notice: `Could not start recording: ${err instanceof Error ? err.message : String(err)}`,
        profile: null,
      })
      this.engine = null
      this.releaseOwnShare()
      // The room was told we record; untell it.
      void this.request((s, done) => s.emit('recording:stop', done))
    } finally {
      patch(this.store, { starting: false })
    }
  }

  /**
   * Stop. The room hears first, so the indicator goes out for everyone at
   * once; then the recorders stop and the last parts go up, which can take
   * a few seconds — the indicator says "finishing" meanwhile.
   */
  async stop(opts: { tellRoom?: boolean } = {}): Promise<void> {
    const current = this.engine
    if (!current) return
    this.engine = null
    patch(this.store, { finishing: true })
    const recordingId = this.room.get().recordingId ?? null
    if (opts.tellRoom !== false) await this.request((s, done) => s.emit('recording:stop', done))
    try {
      await current.stop()
      patch(this.store, {
        notice: 'Recording saved. It is being prepared and will appear under Recordings.',
        lastSavedId: recordingId,
      })
    } catch (err) {
      patch(this.store, {
        notice: `The recording could not be fully uploaded: ${err instanceof Error ? err.message : String(err)}. What was saved on this device can be sent from the recordings page.`,
      })
    } finally {
      patch(this.store, { finishing: false, progress: null, profile: null })
      this.releaseOwnShare()
    }
  }

  /** A moderator ends someone else's recording; their browser finishes the upload. */
  stopTheirs(): Promise<AckResult<void>> {
    return this.request((s, done) => s.emit('recording:stop', done))
  }

  // ----------------------------------------------------- crash buffer

  private setUnfinished(fileId: string, change: Partial<UnfinishedUpload>): void {
    patch(this.store, {
      unfinished: this.s.unfinished.map((u) => (u.fileId === fileId ? { ...u, ...change } : u)),
    })
  }

  async checkUnfinished(): Promise<void> {
    const files = await crashBuffer.unfinishedFiles()
    const current = this.room.get().recordingId
    patch(this.store, {
      unfinished: files
        .filter((f) => !this.engine || f.recordingId !== current)
        .map((f) => ({ ...f, busy: false, error: null })),
    })
  }

  /** Send what the disk still holds, through HTTP with the account that recorded. */
  async resumeUpload(fileId: string): Promise<void> {
    const entry = this.s.unfinished.find((u) => u.fileId === fileId)
    if (!entry || entry.busy) return
    if (!this.auth.isAuthenticated) {
      this.setUnfinished(fileId, {
        error: 'Sign in with the account that recorded to finish this upload.',
      })
      return
    }
    this.setUnfinished(fileId, { busy: true, error: null })
    try {
      // A meeting recording and a video share the crash buffer; their addresses differ.
      const base = entry.kind === 'capture' ? `/videos/${fileId}` : `/recordings/${fileId}`
      await PartUploader.resume(entry, {
        addressFor: (n) => this.http.post<AttachmentUpload>(`${base}/parts/${n}`),
        complete: (durationMs) =>
          this.http.post<void>(`${base}/complete`, durationMs !== null ? { durationMs } : {}),
      })
      patch(this.store, { unfinished: this.s.unfinished.filter((u) => u.fileId !== fileId) })
    } catch (err) {
      this.setUnfinished(fileId, { error: err instanceof Error ? err.message : String(err) })
    } finally {
      this.setUnfinished(fileId, { busy: false })
    }
  }

  async discardUnfinished(fileId: string): Promise<void> {
    const entry = this.s.unfinished.find((u) => u.fileId === fileId)
    // A discarded video is told to the server too, so no half-uploaded row lingers.
    if (entry?.kind === 'capture' && this.auth.isAuthenticated) {
      await this.http.post<void>(`/videos/${fileId}/abort`).catch(() => undefined)
    }
    await crashBuffer.forgetFile(fileId)
    patch(this.store, { unfinished: this.s.unfinished.filter((u) => u.fileId !== fileId) })
  }
}

/** Two rising tones, for everyone, when a recording starts. */
function chime(): void {
  try {
    const ctx = new AudioContext()
    const play = (freq: number, at: number): void => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = freq
      gain.gain.setValueAtTime(0.0001, ctx.currentTime + at)
      gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + at + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + at + 0.35)
      osc.connect(gain).connect(ctx.destination)
      osc.start(ctx.currentTime + at)
      osc.stop(ctx.currentTime + at + 0.4)
    }
    play(660, 0)
    play(880, 0.18)
    setTimeout(() => void ctx.close(), 1200)
  } catch {
    // No audio context: the red indicator still says it.
  }
}
